/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSubscriber } from 'svelte/reactivity';
import { type Atom, type ExtractAtomValue } from 'jotai/vanilla';

import { useStore, type JotaiStore, type UseStoreOptions } from './JotaiStoreContext';

export interface AtomValue<T> {
  current: T;
}

export function useAtomValue<TValue>(
  atom: Atom<TValue>,
  options?: UseStoreOptions,
): AtomValue<TValue>;
export function useAtomValue<AtomType extends Atom<unknown>>(
  atom: AtomType,
  options?: UseStoreOptions,
): AtomValue<ExtractAtomValue<AtomType>>;

export function useAtomValue<TValue>(atom: Atom<TValue>, options?: UseStoreOptions) {
  const store = useStore(options);
  const atomSubscriptionMap = getAtomSubscriptionsMap(store);
  let subscribedAtom = atomSubscriptionMap.get(atom);
  if (!subscribedAtom) {
    subscribedAtom = new AtomSubscription(atom, store);
    atomSubscriptionMap.set(atom, subscribedAtom);
  }
  return subscribedAtom;
}

const storeAtomSubscriptionsMap = new WeakMap<
  JotaiStore,
  WeakMap<Atom<any>, AtomSubscription<Atom<any>>>
>();

function getAtomSubscriptionsMap(store: JotaiStore) {
  let atomValueMap = storeAtomSubscriptionsMap.get(store);
  if (!atomValueMap) {
    atomValueMap = new WeakMap();
    storeAtomSubscriptionsMap.set(store, atomValueMap);
  }
  return atomValueMap;
}

class AtomSubscription<T extends Atom<any>> implements AtomValue<ExtractAtomValue<T>> {
  #atom: T;
  #store: JotaiStore;
  #subscribe;

  constructor(atom: T, store: JotaiStore) {
    this.#atom = atom;
    this.#store = store;
    this.#subscribe = createSubscriber((update) => {
      return this.#store.sub(atom, update);
    });
  }

  get current(): ExtractAtomValue<T> {
    this.#subscribe();
    return this.#store.get(this.#atom);
  }
}
